import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faTrash, faChevronDown, faChevronRight, faSpinner, faFilter,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import ConfirmModal from './ConfirmModal';
import PaymentRelancePrompt from './PaymentRelancePrompt';

const STEPS = [
  { key: 'ao', label: 'Avant-Vente (AO)', accent: '#991b1b' },
  { key: 'setup', label: 'Setup', accent: '#f59e0b' },
  { key: 'terrain', label: 'Terrain', accent: '#10b981' },
  { key: 'reporting', label: 'Reporting', accent: '#0ea5e9' },
  { key: 'closing', label: 'Closing', accent: '#8b5cf6' },
];

const PROTECTED_CLOSING = ['Livrables', 'Réception', 'Facturation', 'Encaissé', 'Attestation de référence'];
const isProtectedItem = (item) => item.step === 'closing' && PROTECTED_CLOSING.includes(item.label);

const ChecklistTab = ({ projectId, readOnly }) => {
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [newLabels, setNewLabels] = useState({});
  const [addingStep, setAddingStep] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'project', or slot id
  const [addTarget, setAddTarget] = useState('project'); // 'project' or slot id
  // Ouvert quand cocher « Facturation » fait basculer l'élément en attente de paiement.
  const [relancePrompt, setRelancePrompt] = useState({ open: false, slotId: null, name: '', mode: undefined });

  const fetchItems = useCallback(async (isInitial) => {
    try {
      if (isInitial) setInitialLoading(true);
      // Fetch all items including slot ones
      const res = await api.get(`/projects/${projectId}/checklists`, { params: { include_slot_items: '1' } });
      setItems(res.data.data || []);
    } catch (err) { console.error('Error:', err); }
    finally { if (isInitial) setInitialLoading(false); }
  }, [projectId]);

  const fetchSlots = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/slots`);
      const data = res.data.data || {};
      const all = [
        ...(data.waves || []).flatMap(w => (w.survey_slots || []).map(s => ({ ...s, waveName: w.name }))),
        ...(data.standalone_slots || []).map(s => ({ ...s, waveName: null })),
      ];
      setSlots(all);
    } catch { }
  }, [projectId]);

  useEffect(() => { fetchItems(true); fetchSlots(); }, [fetchItems, fetchSlots]);

  const handleAdd = async (step, surveySlotId) => {
    const key = surveySlotId ? `slot-${surveySlotId}-${step}` : step;
    const label = (newLabels[key] || '').trim();
    if (!label || addingStep) return;
    setAddingStep(key);
    try {
      const payload = { step, label };
      if (surveySlotId) payload.survey_slot_id = surveySlotId;
      const res = await api.post(`/projects/${projectId}/checklists`, payload);
      setItems(prev => [...prev, res.data.data]);
      setNewLabels(prev => ({ ...prev, [key]: '' }));
    } catch { /* silent — item simply doesn't appear */ }
    finally { setAddingStep(null); }
  };

  const handleToggle = async (itemId) => {
    if (togglingId) return;
    setTogglingId(itemId);
    setItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      return { ...item, is_completed: !item.is_completed, completed_at: !item.is_completed ? new Date().toISOString() : null, completed_by: !item.is_completed ? { name: 'Vous' } : null };
    }));
    try {
      const res = await api.post(`/projects/${projectId}/checklists/${itemId}/toggle`);
      setItems(prev => prev.map(item => item.id === itemId ? res.data.data : item));

      const closing = res.data.closing;
      if (!readOnly && closing?.closing_step === 'attente_paiement' && closing.payment_relance?.is_default) {
        const toggled = items.find(i => i.id === itemId);
        const slotId = toggled?.survey_slot_id || null;
        setRelancePrompt({
          open: true,
          slotId,
          name: slotId ? (slots.find(s => s.id === slotId)?.name || '') : '',
          mode: closing.payment_relance?.mode,
        });
      }
    } catch { fetchItems(false); }
    finally { setTogglingId(null); }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = async (itemId) => {
    if (deletingId) return;
    setDeletingId(itemId);
    const backup = items;
    setItems(prev => prev.filter(item => item.id !== itemId));
    try { await api.delete(`/projects/${projectId}/checklists/${itemId}`); }
    catch { setItems(backup); }
    finally { setDeletingId(null); setDeleteConfirm(null); }
  };

  const confirmDelete = (itemId) => setDeleteConfirm(itemId);

  const toggle = (step) => setCollapsed(prev => ({ ...prev, [step]: !prev[step] }));

  if (initialLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" /></div>;
  }

  // Filter items
  const slotsWithChecklists = slots.filter(s => items.some(i => i.survey_slot_id === s.id));
  const hasSlotItems = slotsWithChecklists.length > 0;

  let filteredItems = items;
  if (filter === 'project') {
    filteredItems = items.filter(i => !i.survey_slot_id);
  } else if (filter !== 'all') {
    filteredItems = items.filter(i => i.survey_slot_id === parseInt(filter));
  }

  const totalItems = filteredItems.length;
  const totalDone = filteredItems.filter(i => i.is_completed).length;

  return (
    <div className="checklist-tab">
      <PaymentRelancePrompt
        isOpen={relancePrompt.open}
        onClose={() => setRelancePrompt({ open: false, slotId: null, name: '', mode: undefined })}
        projectId={projectId}
        slotId={relancePrompt.slotId}
        itemName={relancePrompt.name}
        currentMode={relancePrompt.mode}
      />

      {/* Summary + filter */}
      <div className="checklist-header-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: '0.82rem', color: '#374151' }}>
            <strong>{totalDone}</strong><span style={{ color: '#9ca3af' }}>/{totalItems}</span>
          </div>
          {totalItems > 0 && (
            <div style={{ width: 100, height: 5, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                width: `${(totalDone / totalItems) * 100}%`, height: '100%', borderRadius: 3,
                background: totalDone === totalItems ? '#10b981' : '#dc2626', transition: 'width 0.3s',
              }} />
            </div>
          )}
        </div>

        {(hasSlotItems || slotsWithChecklists.length > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FontAwesomeIcon icon={faFilter} size="xs" style={{ color: '#9ca3af' }} />
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ fontSize: '0.72rem', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', color: '#374151', background: '#fff', cursor: 'pointer' }}
            >
              <option value="all">Tout afficher</option>
              <option value="project">Projet uniquement</option>
              {slotsWithChecklists.map(s => (
                <option key={s.id} value={s.id}>
                  {s.waveName ? `${s.waveName} › ` : ''}{s.name}
                </option>
              ))}
              {/* Also show slots without checklists yet so user can add */}
              {slots.filter(s => !slotsWithChecklists.find(sw => sw.id === s.id)).map(s => (
                <option key={s.id} value={s.id}>
                  {s.waveName ? `${s.waveName} › ` : ''}{s.name} (vide)
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {STEPS.map(({ key, label, accent }) => {
        const stepItems = filteredItems.filter(i => i.step === key);

        // Group: project items first, then by slot
        const projectItems = stepItems.filter(i => !i.survey_slot_id);
        const slotGroups = {};
        stepItems.filter(i => i.survey_slot_id).forEach(i => {
          if (!slotGroups[i.survey_slot_id]) slotGroups[i.survey_slot_id] = [];
          slotGroups[i.survey_slot_id].push(i);
        });

        const hasContent = projectItems.length > 0 || Object.keys(slotGroups).length > 0;
        const doneCount = stepItems.filter(i => i.is_completed).length;
        const isCollapsed = collapsed[key];

        const addSlotId = addTarget !== 'project' ? parseInt(addTarget) : null;

        return (
          <div key={key} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
            {/* Header */}
            <div onClick={() => toggle(key)} style={{
              display: 'flex', alignItems: 'center', padding: '10px 16px', cursor: 'pointer',
              borderBottom: isCollapsed ? 'none' : '1px solid #f3f4f6',
            }}>
              <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} size="xs" style={{ color: '#9ca3af', marginRight: 10 }} />
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: accent, marginRight: 8 }} />
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', flex: 1 }}>{label}</span>
              {stepItems.length > 0 && (
                <>
                  <span style={{ fontSize: '0.65rem', color: '#9ca3af', marginRight: 8 }}>{doneCount}/{stepItems.length}</span>
                  <div style={{ width: 40, height: 3, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      width: `${stepItems.length > 0 ? (doneCount / stepItems.length) * 100 : 0}%`,
                      height: '100%', background: doneCount === stepItems.length ? '#10b981' : accent, borderRadius: 2,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                </>
              )}
            </div>

            {!isCollapsed && (
              <div style={{ padding: '4px 16px 12px' }}>
                {/* Project-level items — grouped by group_label */}
                {projectItems.length > 0 && (filter === 'all' || filter === 'project') && (() => {
                  const ungrouped = projectItems.filter(i => !i.group_label);
                  const byGroup = {};
                  projectItems.filter(i => i.group_label).forEach(i => {
                    if (!byGroup[i.group_label]) byGroup[i.group_label] = [];
                    byGroup[i.group_label].push(i);
                  });

                  return (
                    <div style={{ marginBottom: Object.keys(slotGroups).length > 0 ? 10 : 0 }}>
                      {filter === 'all' && Object.keys(slotGroups).length > 0 && ungrouped.length > 0 && (
                        <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, marginTop: 4 }}>Projet</div>
                      )}
                      {ungrouped.map(item => (
                        <ChecklistRow key={item.id} item={item} onToggle={handleToggle} onDelete={confirmDelete} deletingId={deletingId} readOnly={readOnly} />
                      ))}
                      {Object.entries(byGroup).map(([groupName, groupItems]) => (
                        <div key={groupName} style={{ marginTop: 8, background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }}>
                          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 3, height: 10, borderRadius: 1, background: '#dc2626' }} />
                            {groupName}
                            <span style={{ color: '#9ca3af', fontWeight: 500 }}>({groupItems.filter(i => i.is_completed).length}/{groupItems.length})</span>
                          </div>
                          {groupItems.map(item => (
                            <ChecklistRow key={item.id} item={item} onToggle={handleToggle} onDelete={confirmDelete} deletingId={deletingId} readOnly={readOnly} />
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Slot-level items grouped */}
                {Object.entries(slotGroups).map(([slotId, slotItems]) => {
                  const slot = slots.find(s => s.id === parseInt(slotId));
                  return (
                    <div key={slotId} style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 3, height: 10, borderRadius: 1, background: '#d1d5db' }} />
                        {slot ? (slot.waveName ? `${slot.waveName} › ${slot.name}` : slot.name) : `Enquête #${slotId}`}
                      </div>
                      {slotItems.map(item => (
                        <ChecklistRow key={item.id} item={item} onToggle={handleToggle} onDelete={confirmDelete} deletingId={deletingId} readOnly={readOnly} />
                      ))}
                    </div>
                  );
                })}

                {/* Add inline */}
                {!readOnly && (
                  <div className="checklist-add-row">
                    <input
                      type="text"
                      value={newLabels[addSlotId ? `slot-${addSlotId}-${key}` : key] || ''}
                      onChange={e => {
                        const k = addSlotId ? `slot-${addSlotId}-${key}` : key;
                        setNewLabels(prev => ({ ...prev, [k]: e.target.value }));
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') handleAdd(key, addSlotId); }}
                      placeholder="Ajouter un élément..."
                      style={{ flex: 1, fontSize: '0.75rem', padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none' }}
                    />
                    <select
                      value={addTarget}
                      onChange={e => setAddTarget(e.target.value)}
                      style={{ fontSize: '0.68rem', padding: '5px 6px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', color: '#374151', background: '#fff', cursor: 'pointer', maxWidth: 140 }}
                    >
                      <option value="project">Projet</option>
                      {slots.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.waveName ? `${s.waveName} › ` : ''}{s.name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleAdd(key, addSlotId)}
                      disabled={!(newLabels[addSlotId ? `slot-${addSlotId}-${key}` : key] || '').trim() || addingStep}
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: 'none',
                        background: (newLabels[addSlotId ? `slot-${addSlotId}-${key}` : key] || '').trim() ? '#dc2626' : '#e5e7eb',
                        color: (newLabels[addSlotId ? `slot-${addSlotId}-${key}` : key] || '').trim() ? '#fff' : '#9ca3af',
                        cursor: 'pointer', fontSize: '0.72rem',
                      }}
                    >
                      {addingStep ? <FontAwesomeIcon icon={faSpinner} size="xs" spin /> : <FontAwesomeIcon icon={faPlus} size="xs" />}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm)}
        title="Supprimer cet élément ?"
        message="Cette action est irréversible."
        variant="danger"
        confirmLabel="Supprimer"
      />

      <style>{`
        .checklist-header-bar {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px 16px; margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .checklist-add-row {
          display: flex; align-items: center; gap: 6px; margin-top: 8px; flex-wrap: wrap;
        }
        @media (max-width: 768px) {
          .checklist-header-bar { flex-direction: column; align-items: stretch; gap: 8px; padding: 10px 12px; }
          .checklist-add-row { flex-direction: column; align-items: stretch; }
          .checklist-add-row input { width: 100% !important; }
          .checklist-add-row select { max-width: 100% !important; width: 100% !important; }
          .checklist-add-row button { align-self: flex-end; }
        }
      `}</style>
    </div>
  );
};

const ChecklistRow = ({ item, onToggle, onDelete, deletingId, readOnly }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0',
    borderBottom: '1px solid #f9fafb',
    opacity: deletingId === item.id ? 0.4 : 1, transition: 'opacity 0.2s',
  }}>
    <div onClick={() => !readOnly && onToggle(item.id)} style={{
      width: 16, height: 16, borderRadius: 3, flexShrink: 0, cursor: readOnly ? 'default' : 'pointer',
      border: item.is_completed ? 'none' : '2px solid #d1d5db',
      background: item.is_completed ? '#dc2626' : '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
    }}>
      {item.is_completed && <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <span style={{
        fontSize: '0.78rem', color: item.is_completed ? '#9ca3af' : '#111827',
        textDecoration: item.is_completed ? 'line-through' : 'none',
        fontWeight: item.is_completed ? 400 : 500,
      }}>
        {item.label}
      </span>
      {item.description && (
        <div style={{ fontSize: '0.68rem', color: '#9e9e9e', marginTop: 1, lineHeight: 1.4 }}>
          {item.description}
        </div>
      )}
      {item.is_completed && item.completed_by && (
        <div style={{ fontSize: '0.62rem', color: '#d1d5db', marginTop: 1 }}>
          {item.completed_by.name} {item.completed_at ? `· ${new Date(item.completed_at).toLocaleDateString('fr-FR')}` : ''}
        </div>
      )}
    </div>
    {!readOnly && !isProtectedItem(item) && (
      <button onClick={() => onDelete(item.id)} disabled={deletingId === item.id}
        style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 2, transition: 'color 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.color = '#ef4444'} onMouseLeave={e => e.currentTarget.style.color = '#e5e7eb'}>
        <FontAwesomeIcon icon={faTrash} size="xs" />
      </button>
    )}
  </div>
);

export default ChecklistTab;
